// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 11-observability.md#pod-diagnostics
// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { RefreshCw, Stethoscope } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { AppDiagnostics } from '@greenlight/shared';

import { relativeTime, useTick } from '@/components/greenlight/relativeTime';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { AD_CARD, AD_CARD_HD } from '@/components/greenlight/surfaces';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { thrownCode, thrownMessage } from '@/lib/apiErrors';
import { cn } from '@/lib/utils';

import { AdEyebrow, EnvNameChip, TabInlineEmpty, TabLoadingRows } from './tab-primitives';

type Workload = NonNullable<AppDiagnostics['workload']>;
type Probes = NonNullable<AppDiagnostics['probes']>;
type EnvEntry = NonNullable<AppDiagnostics['env_contract']>[number];
type Connection = NonNullable<AppDiagnostics['connectivity']>[number];
type Freshness = NonNullable<AppDiagnostics['freshness']>;
type Blocker = NonNullable<AppDiagnostics['rollout_blocker']>;

/**
 * The runtime-health snapshot from `GET /api/apps/:id/diagnostics`. Each read
 * runs live cluster reads and reachability probes and is rate limited, so the
 * panel reads once on mount and again only when the reader presses Refresh.
 */
export function AppDiagnosticsPanel({ appId }: { appId: string }): React.JSX.Element | null {
  const key = ['diagnostics', appId] as const;
  const { data, error, isError, isLoading, isFetching, dataUpdatedAt, refetch } =
    api.diagnostics.get.useQuery(
      key,
      { params: { id: appId } },
      { queryKey: key, staleTime: Infinity, refetchOnWindowFocus: false, retry: false },
    );
  useTick(30_000);

  const snapshot = data?.status === 200 ? data.body : undefined;
  const code = isError ? thrownCode(error) : undefined;
  if (code === 'auth.forbidden') return null;
  const notDeployed = code === 'app.not_deployed';

  let body: React.ReactNode;
  if (isLoading) {
    body = <TabLoadingRows />;
  } else if (notDeployed) {
    body = (
      <div data-testid="diagnostics-empty">
        <TabInlineEmpty message="Nothing is running yet. Diagnostics appear once the app has a deployed workload." />
      </div>
    );
  } else {
    body = (
      <div className="space-y-5">
        {isError && (
          <Alert variant="destructive" data-testid="diagnostics-error">
            <AlertTitle>Couldn&apos;t read diagnostics</AlertTitle>
            <AlertDescription>
              {thrownMessage(error, 'Diagnostics are unavailable right now.')}
            </AlertDescription>
          </Alert>
        )}
        {snapshot && <DiagnosticsContent appId={appId} snapshot={snapshot} />}
      </div>
    );
  }

  return (
    <div className={AD_CARD} data-testid="diagnostics-panel">
      <div className={AD_CARD_HD}>
        <div className="flex items-center gap-2.5">
          <span className="grid h-7 w-7 place-items-center rounded-gl-icon bg-glow text-verdant">
            <Stethoscope size={14} strokeWidth={1.6} aria-hidden />
          </span>
          <span className="text-[14px] font-semibold tracking-[-0.01em] text-foreground">
            Diagnostics
          </span>
        </div>
        <div className="flex items-center gap-3">
          {snapshot && dataUpdatedAt > 0 && (
            <span className="hidden text-[12px] text-muted-foreground sm:inline">
              Checked {relativeTime(new Date(dataUpdatedAt).toISOString())}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
            disabled={isFetching}
            aria-label="Refresh diagnostics"
          >
            <RefreshCw size={14} className={cn(isFetching && 'animate-spin')} aria-hidden />
            Refresh
          </Button>
        </div>
      </div>
      <div className="p-5 md:p-6">{body}</div>
    </div>
  );
}

function DiagnosticsContent({
  appId,
  snapshot: d,
}: {
  appId: string;
  snapshot: AppDiagnostics;
}): React.JSX.Element {
  return (
    <>
      {d.rollout_blocker && <BlockerAlert blocker={d.rollout_blocker} />}
      {d.crash_snapshot_ref && (
        <p className="text-[13px]">
          <Link
            className="font-medium text-foreground underline underline-offset-2"
            to={`/apps/${appId}/runs/${d.crash_snapshot_ref.run_id}`}
          >
            View the crash snapshot from the last deploy
          </Link>
        </p>
      )}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <Section title="Workload" testId="diagnostics-workload">
          {d.workload ? <WorkloadRows workload={d.workload} /> : <Unavailable />}
        </Section>
        <Section title="Probes" testId="diagnostics-probes">
          {d.probes ? <ProbeRows probes={d.probes} /> : <Unavailable />}
        </Section>
      </div>
      <Section title="Rollout freshness" testId="diagnostics-freshness">
        {d.freshness ? <FreshnessRows freshness={d.freshness} /> : <Unavailable />}
      </Section>
      <Section title="Env contract" testId="diagnostics-env">
        {d.env_contract ? <EnvContract entries={d.env_contract} /> : <Unavailable />}
      </Section>
      <Section title="Dependencies" testId="diagnostics-connectivity">
        {d.connectivity ? <Connectivity connections={d.connectivity} /> : <Unavailable />}
      </Section>
    </>
  );
}

const BLOCKER_OWNER_TITLE: Record<Blocker['owner'], string> = {
  operator: 'Rollout blocked: IT needs to act',
  agent: 'Rollout blocked: the fix is in the app',
  unknown: 'Rollout blocked',
};

function BlockerAlert({ blocker }: { blocker: Blocker }): React.JSX.Element {
  return (
    <Alert variant="warning" data-testid="diagnostics-blocker">
      <AlertTitle>{BLOCKER_OWNER_TITLE[blocker.owner]}</AlertTitle>
      <AlertDescription>
        <p>{blocker.message}</p>
      </AlertDescription>
    </Alert>
  );
}

function Section({
  title,
  testId,
  children,
}: {
  title: string;
  testId: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="rounded-gl-icon border border-border p-4" data-testid={testId}>
      <AdEyebrow className="mb-3">{title}</AdEyebrow>
      {children}
    </section>
  );
}

function Unavailable(): React.JSX.Element {
  return (
    <p className="text-[13px] text-muted-foreground">
      Unavailable: this couldn&apos;t be read on the last check.
    </p>
  );
}

function Facts({ rows }: { rows: [string, React.ReactNode][] }): React.JSX.Element {
  return (
    <dl className="space-y-1.5 text-[13px]">
      {rows.map(([label, value]) => (
        <div key={label} className="flex flex-wrap items-baseline justify-between gap-x-3">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="min-w-0 break-words text-right text-foreground">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Status({ variant, label }: { variant: StatusBadgeVariant; label: string }) {
  return <StatusBadge variant={variant}>{label}</StatusBadge>;
}

function formatWhen(iso: string | null): string {
  if (iso === null) return 'unknown time';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function terminationText(t: NonNullable<Workload['last_terminated']>): string {
  const parts: string[] = [];
  if (t.oom_killed) parts.push('out of memory');
  if (t.exit_code !== null) parts.push(`exit code ${t.exit_code}`);
  if (t.signal !== null) parts.push(`signal ${t.signal}`);
  const what = parts.length > 0 ? parts.join(', ') : 'reason not reported';
  return `${what} at ${formatWhen(t.at)}`;
}

function WorkloadRows({ workload: w }: { workload: Workload }): React.JSX.Element {
  const rows: [string, React.ReactNode][] = [
    ['Phase', w.phase],
    [
      'Ready',
      w.ready ? (
        <Status variant="active" label="Ready" />
      ) : (
        <Status variant="failed" label="Not ready" />
      ),
    ],
    ['Restarts', <span className="tabular-nums">{w.restart_count}</span>],
  ];
  if (w.last_restart_reason) {
    rows.push(['Last restart reason', <span className="font-mono">{w.last_restart_reason}</span>]);
  }
  if (w.last_terminated) rows.push(['Last exit', terminationText(w.last_terminated)]);
  if (w.started_at) rows.push(['Started', formatWhen(w.started_at)]);
  return <Facts rows={rows} />;
}

function ProbeRow({
  label,
  probe,
}: {
  label: string;
  probe: Probes['readiness'];
}): React.JSX.Element {
  const failures =
    probe.recent_failures === 1 ? '1 recent failure' : `${probe.recent_failures} recent failures`;
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center justify-between gap-2 text-[13px]">
        <span className="text-muted-foreground">{label}</span>
        <span className="flex items-center gap-2">
          {probe.ok ? (
            <Status variant="active" label="Passing" />
          ) : (
            <Status variant="failed" label="Failing" />
          )}
          {probe.recent_failures > 0 && (
            <span className="text-[12px] text-muted-foreground">{failures}</span>
          )}
        </span>
      </div>
      {probe.message && (
        <p className="break-words font-mono text-[12px] text-foreground">{probe.message}</p>
      )}
    </div>
  );
}

function ProbeRows({ probes }: { probes: Probes }): React.JSX.Element {
  return (
    <div className="space-y-3">
      <ProbeRow label="Readiness" probe={probes.readiness} />
      <ProbeRow label="Liveness" probe={probes.liveness} />
    </div>
  );
}

function tri(value: boolean | null, yes: string, no: string): React.ReactNode {
  if (value === null) return <Status variant="inactive" label="Unknown" />;
  return value ? <Status variant="active" label={yes} /> : <Status variant="degraded" label={no} />;
}

function FreshnessRows({ freshness: f }: { freshness: Freshness }): React.JSX.Element {
  return (
    <Facts
      rows={[
        ['Image', tri(f.image_up_to_date, 'Up to date', 'Behind the last merge')],
        ['Env values', tri(f.env_secret_current, 'Current', 'Behind the vault')],
        ['Running image', <span className="font-mono">{f.running_image_tag ?? 'unknown'}</span>],
        [
          'Last merged image',
          <span className="font-mono">{f.expected_image_tag ?? 'unknown'}</span>,
        ],
      ]}
    />
  );
}

const ENV_SOURCE_LABEL: Record<EnvEntry['source'], string> = {
  declared: 'Declared',
  managed: 'Managed',
  integration: 'Integration',
};

const ENV_STATUS: Record<EnvEntry['status'], { variant: StatusBadgeVariant; label: string }> = {
  ok: { variant: 'active', label: 'OK' },
  missing: { variant: 'failed', label: 'Missing' },
  stale: { variant: 'degraded', label: 'Stale' },
};

const ENV_ORDER: Record<EnvEntry['status'], number> = { missing: 0, stale: 1, ok: 2 };

function EnvContract({ entries }: { entries: EnvEntry[] }): React.JSX.Element {
  if (entries.length === 0) {
    return <p className="text-[13px] text-muted-foreground">This app expects no env names.</p>;
  }
  const missing = entries.filter((e) => e.status === 'missing').length;
  const stale = entries.filter((e) => e.status === 'stale').length;
  const sorted = [...entries].sort(
    (a, b) => ENV_ORDER[a.status] - ENV_ORDER[b.status] || a.name.localeCompare(b.name),
  );
  return (
    <div className="space-y-3">
      <p className="text-[13px] text-foreground">
        {missing + stale === 0
          ? 'Every expected name is in the pod.'
          : `${missing} missing, ${stale} stale. A missing name has no value the pod can receive; a stale one was changed after the last deploy and reaches the pod on the next merge.`}
      </p>
      <ul className="divide-y divide-border/60">
        {sorted.map((e) => (
          <li
            key={e.name}
            className="flex flex-wrap items-center justify-between gap-2 py-2 text-[13px]"
          >
            <EnvNameChip name={e.name} />
            <span className="flex items-center gap-2">
              <span className="text-[12px] text-muted-foreground">
                {ENV_SOURCE_LABEL[e.source]}
              </span>
              <Status {...ENV_STATUS[e.status]} />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Connectivity({ connections }: { connections: Connection[] }): React.JSX.Element {
  if (connections.length === 0) {
    return (
      <p className="text-[13px] text-muted-foreground">This app has no dependencies to probe.</p>
    );
  }
  return (
    <ul className="divide-y divide-border/60">
      {connections.map((c) => (
        <li
          key={c.target}
          className="flex flex-wrap items-center justify-between gap-2 py-2 text-[13px]"
        >
          <span className="min-w-0">
            <span className="font-medium text-foreground">{c.target}</span>
            <span className="ml-2 break-all font-mono text-[12px] text-muted-foreground">
              {c.host ?? 'host did not resolve'}
            </span>
          </span>
          <span className="flex flex-wrap items-center gap-2">
            {c.reachable ? (
              <Status variant="active" label="Reachable" />
            ) : (
              <Status variant="failed" label="Unreachable" />
            )}
            {!c.allowed_by_pod_egress && <Status variant="failed" label="Blocked by egress" />}
            {c.latency_ms !== null && (
              <span className="tabular-nums text-[12px] text-muted-foreground">
                {Math.round(c.latency_ms)} ms
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}
